import { Receipt, CheckCircle } from '@mui/icons-material';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import InfoIcon from '@mui/icons-material/Info';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Grid,
  MenuItem,
  Paper,
  TextField,
  Typography,
  InputAdornment,
} from "@mui/material";

import moment from "moment";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import configurationService from "../../service/configurationService";
import accountReceivableService from "../../service/finances/accountReceivableService";
import saleService from "../../service/saleService";
import { formatCnpjCpf } from "../../utils/formatters";
import ResponsiveDialog from "../dialog/dialog";
import DrawerPrincipal from "../drawer/drawer-principal";
import SimpleBackdrop from "../loading/loading";

function RecordInvoice() {
  const navigate = useNavigate();
  const location = useLocation();
  const { codIntern: codInternFromState, idCurrent: idCurrentFromState } = location.state || {};
  
  const [configurations, setConfigurations] = useState([]);
  const [loadingConfigurations, setLoadingConfigurations] = useState(false);
  const [selectedConfigId, setSelectedConfigId] = useState("");

  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceNumberError, setInvoiceNumberError] = useState("");
  const [isCheckingInvoice, setIsCheckingInvoice] = useState(false);
  const [isInvoiceAlreadyRecorded, setIsInvoiceAlreadyRecorded] = useState(false);
  const [isInvoiceLookupLocked, setIsInvoiceLookupLocked] = useState(false);

  const [codIntern, setCodIntern] = useState(codInternFromState || "");
  const [codInternError, setCodInternError] = useState("");

  // idCurrent vem do state e é apenas para visualização (campo desabilitado)
  const idCurrent = idCurrentFromState || "";

  const [saleInfo, setSaleInfo] = useState(null);
  const [loadingSale, setLoadingSale] = useState(false);
  const [saleError, setSaleError] = useState("");

  // const [retainIss, setRetainIss] = useState(false);

  const [openDialog, setOpenDialog] = useState(false);
  const [dialogStatus, setDialogStatus] = useState("success");
  const [dialogTitle, setDialogTitle] = useState("");
  const [dialogMessage, setDialogMessage] = useState("");
  const [shouldNavigateBackOnDialogClose, setShouldNavigateBackOnDialogClose] = useState(false);
  const [openLoading, setOpenLoading] = useState(false);
  const [invoiceNumberTouched, setInvoiceNumberTouched] = useState(false);

  /**
   * Carrega os ambientes de configuração para o select
   */
  useEffect(() => {
    const loadConfigurations = async () => {
      try {
        setLoadingConfigurations(true);
        const response = await configurationService.list();

        if (response && response.success && Array.isArray(response.data)) {
          setConfigurations(response.data);
        } else if (response && Array.isArray(response.data)) {
          // Fallback caso venha num formato diferente
          setConfigurations(response.data);
        }
      } catch (error) {
        console.error("Erro ao carregar configurações:", error);
      } finally {
        setLoadingConfigurations(false);
      }
    };

    loadConfigurations();
  }, []);

  const handleDialogClose = () => {
    setOpenDialog(false);
    if (shouldNavigateBackOnDialogClose) {
      setShouldNavigateBackOnDialogClose(false);
      navigate(-1);
    }
  };

  const handleCloseLoading = () => setOpenLoading(false);

  /**
   * Permite apenas números no campo
   */
  const handleNumericChange = (setter, setError) => (event) => {
    const value = event.target.value.replace(/\D/g, "");
    setter(value);
    if (setError) {
      setError("");
    }
  };

  const handleInvoiceNumberChange = (event) => {
    handleNumericChange(setInvoiceNumber, setInvoiceNumberError)(event);
    setIsInvoiceAlreadyRecorded(false);
    setIsInvoiceLookupLocked(false);
  };

  /**
   * Verifica se a nota fiscal já foi escriturada
   */
  const checkInvoiceNumber = async () => {
    if (!invoiceNumber || invoiceNumber.trim() === "") return;
    
    try {
      setIsCheckingInvoice(true);
      const response = await accountReceivableService.buscarPorNotaFiscal({
        numeroDocumentoFiscal: invoiceNumber,
        idConfiguration: 1,
        idConfigurationOrigem: selectedConfigId
      });

      console.log("🔍 [RecordInvoice] Resposta da verificação da nota fiscal:", response);

      if (response?.success) {
        const alreadyRecorded =
          response.data?.codigoPedidoCliente && response.data?.numeroContrato;

        setIsInvoiceAlreadyRecorded(!!alreadyRecorded);
        setIsInvoiceLookupLocked(true);

        setDialogStatus("warning");
        setDialogTitle("Atenção");
        setDialogMessage(
          alreadyRecorded
            ? `Essa nota já foi escriturada nesse ambiente e não pode ser registrada novamente.`
            : `Encontramos uma conta a receber para essa nota fiscal.`
        );
        setOpenDialog(true);

        return alreadyRecorded;
      }

      return false; // Nota não escriturada
    } catch (error) {
      console.error("Erro ao verificar nota fiscal:", error);
      return false;
    } finally {
      setIsCheckingInvoice(false);
    }
  };

  /**
   * Buscar venda pelo código interno (e opcionalmente codCurrent).
   * Respeita o formato de retorno:
   * {
   *   success: true,
   *   data: {
   *     vendaPrincipal: { ... },
   *     vendas: [ ... ] // array completo (1 para não recorrente, N para recorrente)
   *   }
   * }
   */
  const fetchSale = async () => {
    if (!codIntern) {
      setCodInternError("Informe o código interno");
      return;
    }

    try {
      setLoadingSale(true);
      setSaleError("");
      // Limpa as informações da venda antes de buscar
      setSaleInfo(null);

      // Sempre usa o idCurrent se estiver disponível (vem do state)
      const codCurrentToUse = idCurrent || null;

      const response = await saleService.getSaleByCodIntern(
        codIntern,
        codCurrentToUse
      );

      console.log("🔍 [RecordInvoice] Resposta da API:", response);
      console.log("🔍 [RecordInvoice] idCurrent usado na busca:", codCurrentToUse);

      // Verifica se a resposta foi bem-sucedida
      if (response && response.success === true) {
        // O retorno pode vir em response.data ou response.data.data
        let responseData = response.data;
        
        // Se response.data tem data dentro e esse data tem vendaPrincipal/vendas, usa ele
        if (response.data?.data?.vendaPrincipal && Array.isArray(response.data?.data?.vendas)) {
          responseData = response.data.data;
        } else if (response.data?.vendaPrincipal && Array.isArray(response.data?.vendas)) {
          responseData = response.data;
        } else {
          // Caso contrário, tenta response.data.data ou response.data
          responseData = response.data?.data || response.data;
        }
        
        console.log("📦 [RecordInvoice] Dados recebidos:", responseData);

        // Formato quando há vendas recorrentes: { vendaPrincipal: {...}, vendas: Array }
        if (responseData?.vendaPrincipal && Array.isArray(responseData?.vendas)) {
          const { vendaPrincipal } = responseData;
          // Como já temos o idCurrent, sempre mostra a venda principal
          console.log("✅ [RecordInvoice] Venda encontrada (formato recorrente)");
          setSaleInfo(vendaPrincipal);
          return;
        }

        // Formato alternativo: array direto ou objeto único
        let vendasArray = [];
        let vendaUnica = null;
        
        if (Array.isArray(responseData?.data)) {
          vendasArray = responseData.data;
        } else if (Array.isArray(responseData)) {
          vendasArray = responseData;
        } else if (responseData?.data && typeof responseData.data === 'object' && !Array.isArray(responseData.data)) {
          if (!responseData.data.vendaPrincipal && !responseData.data.vendas) {
            vendaUnica = responseData.data;
          }
        } else if (responseData && typeof responseData === 'object' && !Array.isArray(responseData)) {
          if (!responseData.vendaPrincipal && !responseData.vendas) {
            vendaUnica = responseData;
          }
        }

        // Se tem venda única, mostra ela
        if (vendaUnica) {
          console.log("✅ [RecordInvoice] Venda única encontrada");
          setSaleInfo(vendaUnica);
        } else if (vendasArray && vendasArray.length > 0) {
          // Se tem array, pega a primeira (ou a que corresponde ao idCurrent se houver)
          if (codCurrentToUse && vendasArray.length > 1) {
            // Tenta encontrar a venda específica pelo idCurrent
            const vendaEncontrada = vendasArray.find(v => v.idCurrent === codCurrentToUse);
            if (vendaEncontrada) {
              console.log("✅ [RecordInvoice] Venda específica encontrada pelo idCurrent");
              setSaleInfo(vendaEncontrada);
            } else {
              console.log("✅ [RecordInvoice] Usando primeira venda do array");
              setSaleInfo(vendasArray[0]);
            }
          } else {
            console.log("✅ [RecordInvoice] Usando primeira venda do array");
            setSaleInfo(vendasArray[0]);
          }
        } else {
          setSaleError("Nenhuma venda encontrada para o código interno.");
          setSaleInfo(null);
        }
      } else {
        // Resposta com erro
        const errorMessage = 
          (response && response.message) ||
          "Erro ao buscar informações da venda.";
        console.error("❌ [RecordInvoice] Erro na resposta:", errorMessage);
        setSaleError(errorMessage);
        setSaleInfo(null);
      }
    } catch (error) {
      console.error("❌ [RecordInvoice] Erro ao buscar venda:", error);
      setSaleError("Erro ao buscar informações da venda.");
      setSaleInfo(null);
    } finally {
      setLoadingSale(false);
    }
  };

  const handleCodInternBlur = () => {
    if (codIntern) {
      fetchSale();
    }
  };


  /**
   * Busca automaticamente a venda quando codIntern e idCurrent são recebidos via state
   */
  useEffect(() => {
    if (codInternFromState) {
      console.log("📥 [RecordInvoice] Dados recebidos via state:", { codIntern: codInternFromState, idCurrent: idCurrentFromState });
      // Aguarda um pouco para garantir que as configurações foram carregadas
      const timer = setTimeout(() => {
        fetchSale();
      }, 500);
      
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codInternFromState, idCurrentFromState]);

  /**
   * Volta para a página anterior ao cancelar
   */
  const handleCancel = () => {
    navigate(-1);
  };

  /**
   * Validação básica do formulário
   */
  const isValid = () => {
    let valid = true;

    if (!selectedConfigId) {
      valid = false;
      setDialogStatus("error");
      setDialogTitle("Ops!");
      setDialogMessage("Selecione um ambiente.");
      setOpenDialog(true);
    }

    if (!invoiceNumber || invoiceNumber.toString().trim() === "") {
      valid = false;
      setInvoiceNumberError("Informe o número da nota fiscal");
    }

    if (!codIntern) {
      valid = false;
      setCodInternError("Informe o código interno");
    }

    if (!saleInfo) {
      // Já tenta buscar caso ainda não tenha buscado
      fetchSale();
      valid = false;
    }

    return valid;
  };

  /**
   * Envia os dados para o backend para registrar a nota fiscal
   */
  const handleSubmit = async () => {
    console.log("🔍 [RecordInvoice] handleSubmit chamado");
    console.log("🔍 [RecordInvoice] invoiceNumber:", invoiceNumber);
    console.log("🔍 [RecordInvoice] codIntern:", codIntern);
    console.log("🔍 [RecordInvoice] selectedConfigId:", selectedConfigId);
    
    if (!isValid()) {
      console.log("❌ [RecordInvoice] Validação falhou");
      return;
    }

    try {
      setOpenLoading(true);
      // Garante que invoiceNumber é uma string não vazia
      const numeroDocumentoFiscal = invoiceNumber?.toString().trim() || "";
      
      console.log("🔍 [RecordInvoice] numeroDocumentoFiscal processado:", numeroDocumentoFiscal);
      
      if (!numeroDocumentoFiscal) {
        setInvoiceNumberError("Informe o número da nota fiscal");
        setDialogStatus("error");
        setDialogTitle("Ops!");
        setDialogMessage("O número da nota fiscal é obrigatório.");
        setOpenDialog(true);
        return;
      }

      const payload = {
        numeroDocumentoFiscal: numeroDocumentoFiscal,
        codIntern: codIntern?.toString() || "",
        idConfiguration: Number(selectedConfigId),
        // API espera "S" ou "N" para retenção de ISS
        // retainIss: retainIss ? "S" : "N",
      };

      // codCurrent é opcional (vem do state quando disponível)
      if (idCurrent) {
        payload.codCurrent = idCurrent.toString();
      }

      console.log("📤 [RecordInvoice] Payload enviado:", payload);

      const response = await accountReceivableService.recordInvoice(payload);

      if (response && response.success) {
        setDialogStatus("success");
        setDialogTitle("Muito bem!");
        setDialogMessage("Nota fiscal escriturada com sucesso.");
        setShouldNavigateBackOnDialogClose(true);
        setOpenDialog(true);
      } else {
        setDialogStatus("error");
        setDialogTitle("Ops!");
        setDialogMessage(
          (response && response.message) ||
            "Erro ao escriturar nota fiscal. Tente novamente."
        );
        setShouldNavigateBackOnDialogClose(false);
        setOpenDialog(true);
      }
    } catch (error) {
      console.error("Erro ao escriturar nota fiscal:", error);
      setDialogStatus("error");
      setDialogTitle("Ops!");
      setDialogMessage("Erro ao escriturar nota fiscal. Tente novamente.");
      setShouldNavigateBackOnDialogClose(false);
      setOpenDialog(true);
    } finally {
      setOpenLoading(false);
    }
  };

  /**
   * Formata data no padrão brasileiro
   */
  const formatDate = (date) => {
    if (!date) return "-";
    return moment(date).format("DD/MM/YYYY");
  };

  /**
   * Obtém valor líquido da venda formatado
   */
  const getNetValue = (sale) => {
    if (!sale) return "-";
    let value = null;
    
    // Tenta diferentes caminhos para obter o valor líquido
    if (sale.vlAFaturar != null) {
      value = sale.vlAFaturar;
    } else if (sale.totalLiquid != null) {
      value = sale.totalLiquid;
    } else if (sale.formPayment && sale.formPayment.vlAmountReceivable != null) {
      value = sale.formPayment.vlAmountReceivable;
    }
    
    if (value == null) return "-";
    
    // Formata como moeda brasileira
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numValue)) return "-";
    
    return numValue.toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL"
    });
  };

  /**
   * Obtém data de previsão
   */
  const getPrevisionDate = (sale) => {
    if (!sale) return null;
    if (sale.formPayment && sale.formPayment.datePrevisionSales) {
      return sale.formPayment.datePrevisionSales;
    }
    return sale.datePrevisionSales || null;
  };

  /**
   * Obtém data de vencimento
   */
  const getDueDate = (sale) => {
    if (!sale) return null;
    if (sale.formPayment && sale.formPayment.dateReceivable) {
      return sale.formPayment.dateReceivable;
    }
    return sale.dateReceivable || null;
  };

  /**
   * Obtém nome do cliente
   */
  const getCustomerName = (sale) => {
    if (!sale) return "-";
    // Tenta diferentes caminhos possíveis para o nome do cliente
    if (sale.customer && sale.customer.name) {
      return sale.customer.name;
    }
    if (sale.customerName) {
      return sale.customerName;
    }
    if (sale.customer && typeof sale.customer === 'string') {
      return sale.customer;
    }
    return "-";
  };

  /**
   * Obtém CPF/CNPJ do cliente
   */
  const getCustomerCpfCnpj = (sale) => {
    if (!sale) return "-";
    // Tenta diferentes caminhos possíveis para o CPF/CNPJ
    if (sale.customer && sale.customer.cpfCnpj) {
      return formatCnpjCpf(sale.customer.cpfCnpj);
    }
    if (sale.cpfCnpj) {
      return formatCnpjCpf(sale.cpfCnpj);
    }
    if (sale.customer && sale.customer.cpf) {
      return formatCnpjCpf(sale.customer.cpf);
    }
    if (sale.customer && sale.customer.cnpj) {
      return formatCnpjCpf(sale.customer.cnpj);
    }
    return "-";
  };


  return (
    <DrawerPrincipal
      title={"Escriturar Nota Fiscal"}
      divOpen={
        <div>
          <Container maxWidth="lg" style={{ marginTop: "20px" }}>
            <Paper 
              sx={{ 
                padding: 4, 
                marginTop: 3,
                borderRadius: 4,
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                backgroundColor: '#ffffff'
              }}
            >
              <Box 
                display="flex" 
                alignItems="center" 
                gap={1.5}
                sx={{
                  marginBottom: 3,
                  paddingBottom: 2,
                  borderBottom: '2px solid #e0e0e0'
                }}
              >
                <Receipt color='primary' sx={{ fontSize: 28 }} />
                <Typography 
                  variant="h6" 
                  sx={{
                    fontWeight: 'bold',
                    color: '#1976d2',
                    fontSize: '1.5rem'
                  }}
                >
                  Escrituração de Nota Fiscal
                </Typography>
              </Box>

              <Grid container spacing={3} style={{ marginTop: 8 }}>
                <Grid item xs={12} md={4}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Ambiente"
                    value={selectedConfigId}
                    onChange={(event) =>
                      setSelectedConfigId(event.target.value)
                    }
                    disabled={loadingConfigurations}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        '&:hover fieldset': {
                          borderColor: '#1976d2',
                          borderWidth: 2,
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#1976d2',
                          borderWidth: 2,
                        },
                      },
                      '& .MuiSelect-select': {
                        textAlign: 'left',
                      },
                    }}
                  >
                    {loadingConfigurations && (
                      <MenuItem value="" disabled>
                        Carregando ambientes...
                      </MenuItem>
                    )}
                    {!loadingConfigurations &&
                      configurations.map((config) => (
                        <MenuItem
                          key={config.idconfiguration}
                          value={config.idconfiguration}
                        >
                          {config.environment || config.nameEnvironment}
                        </MenuItem>
                      ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Número da Nota Fiscal"
                    placeholder="Digite o número"
                    value={invoiceNumber}
                    onChange={handleInvoiceNumberChange}
                    onBlur={() => {
                      setInvoiceNumberTouched(true);
                      if (invoiceNumber && invoiceNumber.trim() !== "") {
                        checkInvoiceNumber();
                      }
                    }}
                    error={!!invoiceNumberError || (invoiceNumberTouched && !invoiceNumber?.trim())}
                    helperText={
                      isCheckingInvoice
                        ? "Verificando nota fiscal..."
                        : isInvoiceAlreadyRecorded
                          ? "Essa nota já foi escriturada."
                          : isInvoiceLookupLocked
                            ? "Conta encontrada. Altere o número para continuar."
                            : (invoiceNumberError ||
                              (invoiceNumberTouched && !invoiceNumber?.trim()
                                ? "Preencha o número da nota fiscal para continuar"
                                : ""))
                    }
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          {isCheckingInvoice ? (
                            <CircularProgress size={20} color="primary" />
                          ) : isInvoiceLookupLocked ? (
                            <CheckCircle sx={{ color: 'success.main', fontSize: 22 }} />
                          ) : null}
                        </InputAdornment>
                      )
                    }}
                    InputLabelProps={{
                      sx: {
                        fontSize: '1rem',
                        fontWeight: 500,
                        color: '#666'
                      }
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        '&:hover fieldset': {
                          borderColor: '#1976d2',
                          borderWidth: 2,
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#1976d2',
                          borderWidth: 2,
                        },
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Código Interno"
                    value={codIntern}
                    onChange={handleNumericChange(
                      setCodIntern,
                      setCodInternError
                    )}
                    onBlur={handleCodInternBlur}
                    disabled={!!codInternFromState}
                    error={!!codInternError}
                    InputLabelProps={{
                      sx: {
                        fontSize: '1rem',
                        fontWeight: 500,
                        color: '#666'
                      }
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        '&:hover fieldset': {
                          borderColor: '#1976d2',
                          borderWidth: 2,
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#1976d2',
                          borderWidth: 2,
                        },
                      },
                    }}
                  />
                </Grid>

                {idCurrent && (
                  <>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Recorrente"
                        value={idCurrent}
                        disabled
                        InputLabelProps={{
                          sx: {
                            fontSize: '1rem',
                            fontWeight: 500,
                            color: '#666'
                          }
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 3,
                            backgroundColor: '#f5f5f5',
                          },
                        }}
                      />
                    </Grid>

                    {/* <Grid item xs={12} md={4}>
                      <Box display="flex" alignItems="center" gap={3}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={retainIss}
                              onChange={(event) =>
                                setRetainIss(event.target.checked)
                              }
                            />
                          }
                          label="Retem ISS ?"
                        />
                      </Box>
                    </Grid> */}
                  </>
                )}

                <Grid item xs={12}>
                  {loadingSale && (
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="flex-start"
                      marginTop={2}
                      sx={{
                        padding: 2,
                        borderRadius: 3,
                        backgroundColor: '#e3f2fd',
                        border: '1px solid #1976d2'
                      }}
                    >
                      <CircularProgress
                        size={24}
                        sx={{ marginRight: 2, color: '#1976d2' }}
                      />
                      <Typography 
                        variant="body2"
                        sx={{ 
                          color: '#1976d2',
                          fontWeight: 'medium'
                        }}
                      >
                        Buscando informações da venda...
                      </Typography>
                    </Box>
                  )}

                  {saleError && !loadingSale && (
                    <Typography
                      color="error"
                      variant="body2"
                      sx={{ 
                        marginTop: 2,
                        padding: 2,
                        borderRadius: 3,
                        backgroundColor: '#ffebee',
                        border: '1px solid #d32f2f',
                        fontWeight: 'medium'
                      }}
                    >
                      {saleError}
                    </Typography>
                  )}

                </Grid>

                {saleInfo && (
                  <Grid item xs={12}>
                    <Card
                      sx={{
                        borderRadius: 4,
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                        backgroundColor: '#f5f5f5',
                        borderTopLeftRadius: 4,
                        borderTopRightRadius: 4,
                        padding: 0,
                        overflow: 'hidden'
                      }}
                    >
                      <Box
                        sx={{
                          backgroundColor: '#f5f5f5',
                          padding: 2,
                          borderBottom: '1px solid #e0e0e0'
                        }}
                      >
                        <Box display="flex" alignItems="center" gap={1}>
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <InfoIcon color='primary' sx={{ fontSize: 20 }} />
                          </Box>
                          <Typography 
                            variant="subtitle1" 
                            sx={{
                              fontWeight: 'bold',
                              fontSize: '0.9rem',
                              color: '#333'
                            }}
                          >
                            Informações da Venda
                          </Typography>
                        </Box>
                      </Box>
                      <CardContent sx={{ padding: 3 }}>
                        <Grid container spacing={3}>
                          {/* Primeira linha: Informações do Cliente */}
                          <Grid item xs={12}>
                            <Grid container spacing={3}>
                              <Grid item xs={12} sm={6}>
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    display: 'block',
                                    fontSize: '1rem',
                                    fontWeight: 500,
                                    color: '#666',
                                    marginBottom: 0.5
                                  }}
                                >
                                  Cliente
                                </Typography>
                                <Typography 
                                  variant="body1" 
                                  sx={{ 
                                    fontWeight: 'bold',
                                    fontSize: '0.875rem',
                                    color: '#333'
                                  }}
                                >
                                  {getCustomerName(saleInfo)}
                                </Typography>
                              </Grid>
                              
                              <Grid item xs={12} sm={6}>
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    display: 'block',
                                    fontSize: '1rem',
                                    fontWeight: 500,
                                    color: '#666',
                                    marginBottom: 0.5
                                  }}
                                >
                                  Cpf/Cnpj
                                </Typography>
                                <Typography 
                                  variant="body1" 
                                  sx={{ 
                                    fontWeight: 'bold',
                                    fontSize: '0.875rem',
                                    color: '#333'
                                  }}
                                >
                                  {getCustomerCpfCnpj(saleInfo)}
                                </Typography>
                              </Grid>
                            </Grid>
                          </Grid>

                          {/* Segunda linha: Valor e Datas */}
                          <Grid item xs={12}>
                            <Grid container spacing={3} alignItems="flex-end">
                              <Grid item xs={12} sm={4}>
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    display: 'block',
                                    fontSize: '1rem',
                                    fontWeight: 500,
                                    color: '#666',
                                    marginBottom: 0.5
                                  }}
                                >
                                  Valor Líquido
                                </Typography>
                                <Typography 
                                  variant="h6" 
                                  sx={{ 
                                    fontWeight: 'bold',
                                    fontSize: '0.875rem',
                                    color: '#1976d2'
                                  }}
                                >
                                  {getNetValue(saleInfo)}
                                </Typography>
                              </Grid>

                              <Grid item xs={12} sm={4}>
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    display: 'block',
                                    fontSize: '1rem',
                                    fontWeight: 500,
                                    color: '#666',
                                    marginBottom: 0.5
                                  }}
                                >
                                  Data de Previsão
                                </Typography>
                                <Typography 
                                  variant="body1" 
                                  sx={{ 
                                    fontWeight: 'bold',
                                    fontSize: '0.875rem',
                                    color: '#333'
                                  }}
                                >
                                  {formatDate(getPrevisionDate(saleInfo))}
                                </Typography>
                              </Grid>

                              <Grid item xs={12} sm={4}>
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    display: 'block',
                                    fontSize: '1rem',
                                    fontWeight: 500,
                                    color: '#666',
                                    marginBottom: 0.5
                                  }}
                                >
                                  Data de Vencimento
                                </Typography>
                                <Typography 
                                  variant="body1" 
                                  sx={{ 
                                    fontWeight: 'bold',
                                    fontSize: '0.875rem',
                                    color: '#333'
                                  }}
                                >
                                  {formatDate(getDueDate(saleInfo))}
                                </Typography>
                              </Grid>
                            </Grid>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                )}

                <Grid item xs={12} style={{ marginTop: 24 }}>
                  <Box 
                    display="flex" 
                    justifyContent="flex-end" 
                    gap={2}
                    sx={{
                      flexDirection: { xs: 'column', sm: 'row' },
                      width: '100%'
                    }}
                  >
                    <Button
                      variant="contained"
                      color="warning"
                      onClick={handleCancel}
                      size="medium"
                      startIcon={<HighlightOffIcon />}
                      sx={{ 
                        borderRadius: 3,
                        paddingX: 3,
                        paddingY: 1.5,
                        fontWeight: 'bold',
                        textTransform: 'none',
                        fontSize: '1rem',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                        '&:hover': {
                          boxShadow: '0 6px 8px rgba(0, 0, 0, 0.15)',
                        },
                        width: { xs: '100%', sm: 'auto' }
                      }}
                    >
                      Cancelar
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      size="medium"
                      onClick={handleSubmit}
                      disabled={
                        openLoading ||
                        isCheckingInvoice ||
                        !invoiceNumber?.trim() ||
                        isInvoiceAlreadyRecorded ||
                        isInvoiceLookupLocked
                      }
                      sx={{ 
                        borderRadius: 3,
                        paddingX: 4,
                        paddingY: 1.5,
                        fontWeight: 'bold',
                        textTransform: 'none',
                        fontSize: '1rem',
                        boxShadow: '0 4px 6px rgba(25, 118, 210, 0.3)',
                        '&:hover': {
                          boxShadow: '0 6px 8px rgba(25, 118, 210, 0.4)',
                        },
                        width: { xs: '100%', sm: 'auto' },
                        gap: 1
                      }}
                    >
                      <Receipt sx={{ fontSize: 20 }} />
                      {openLoading ? (
                        <Box display="flex" alignItems="center" gap={1}>
                          <CircularProgress size={20} color="inherit" />
                          Escriturando...
                        </Box>
                      ) : (
                        "Escriturar Nota Fiscal"
                      )}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Paper>

            <ResponsiveDialog
              open={openDialog}
              handleClose={handleDialogClose}
              status={dialogStatus}
              title={dialogTitle}
              message={dialogMessage}
            />

            <SimpleBackdrop
              open={openLoading}
              handleClose={handleCloseLoading}
              title={"Escriturando nota fiscal..."}
            />
          </Container>
        </div>
      }
    />
  );
}

export default RecordInvoice;